import { boolean, createBuilder, createSchema, defineMutatorWithType, defineMutators, defineQueries, defineQueryWithType, enumeration, number, relationships, string, table, } from "@rocicorp/zero";
import { z } from "zod";
import { peopleTable, toZeroDate, } from "./equipment-plan.js";
import { projects, projectZql } from "./projects-schema.js";
const vehicles = table("vehicles")
    .from("vehicles")
    .columns({
    id: string(),
    name: string(),
    brand: string().optional(),
    model: string().optional(),
    vehicleType: enumeration().from("vehicle_type"),
    status: enumeration(),
    licensePlate: string().from("license_plate").optional(),
    registrationNumber: string().from("registration_number").optional(),
    vin: string().optional(),
    firstRegistration: number().from("first_registration").optional(),
    licenseCategory: string().from("license_category").optional(),
    lengthM: number().from("length_m").optional(),
    widthM: number().from("width_m").optional(),
    heightM: number().from("height_m").optional(),
    cargoLengthM: number().from("cargo_length_m").optional(),
    cargoWidthM: number().from("cargo_width_m").optional(),
    cargoHeightM: number().from("cargo_height_m").optional(),
    emptyWeightKg: number().from("empty_weight_kg").optional(),
    grossWeightKg: number().from("gross_weight_kg").optional(),
    payloadKg: number().from("payload_kg").optional(),
    towingCapacityKg: number().from("towing_capacity_kg").optional(),
    seats: number().optional(),
    fuelType: enumeration().from("fuel_type").optional(),
    transmission: enumeration().optional(),
    mileageKm: number().from("mileage_km").optional(),
    hasTowHitch: boolean().from("has_tow_hitch"),
    hasPowerInverter: boolean().from("has_power_inverter"),
    nextInspectionDate: number().from("next_inspection_date").optional(),
    nextServiceDate: number().from("next_service_date").optional(),
    nextServiceKm: number().from("next_service_km").optional(),
    vignetteValidUntil: number().from("vignette_valid_until").optional(),
    insuranceValidUntil: number().from("insurance_valid_until").optional(),
    notes: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
})
    .primaryKey("id");
const vehicleImages = table("vehicleImages")
    .from("vehicle_images")
    .columns({
    id: string(),
    vehicleID: string().from("vehicle_id"),
    imageURL: string().from("image_url"),
    displayOrder: number().from("display_order"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
// Metadata only: file content is private and served by the API
const vehicleDocuments = table("vehicleDocuments")
    .from("vehicle_documents")
    .columns({
    id: string(),
    vehicleID: string().from("vehicle_id"),
    documentType: enumeration().from("document_type"),
    fileName: string().from("file_name"),
    contentType: string().from("content_type"),
    sizeBytes: number().from("size_bytes"),
    uploadedByPersonID: string().from("uploaded_by_person_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const vehicleServiceRecords = table("vehicleServiceRecords")
    .from("vehicle_service_records")
    .columns({
    id: string(),
    vehicleID: string().from("vehicle_id"),
    serviceDate: number().from("service_date"),
    mileageKm: number().from("mileage_km").optional(),
    workshop: string().optional(),
    description: string(),
    cost: number().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const vehicleDamages = table("vehicleDamages")
    .from("vehicle_damages")
    .columns({
    id: string(),
    vehicleID: string().from("vehicle_id"),
    title: string(),
    description: string().optional(),
    severity: enumeration(),
    status: enumeration(),
    reportedOn: number().from("reported_on"),
    reportedByPersonID: string().from("reported_by_person_id").optional(),
    resolvedOn: number().from("resolved_on").optional(),
    imageURL: string().from("image_url").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
// A project booking (category "project") or an appointment without project
const vehicleBookings = table("vehicleBookings")
    .from("vehicle_bookings")
    .columns({
    id: string(),
    vehicleID: string().from("vehicle_id"),
    category: enumeration(),
    projectID: string().from("project_id").optional(),
    title: string().optional(),
    startDate: number().from("start_date"),
    endDate: number().from("end_date"),
    /** Project bookings: days the vehicle's driver is needed (virtual "Driver" function) */
    outboundDate: number().from("outbound_date").optional(),
    returnDate: number().from("return_date").optional(),
    /** Drives on the outbound trip (outbound date) */
    driverPersonID: string().from("driver_person_id").optional(),
    /** Drives on the return trip (return date) */
    returnDriverPersonID: string().from("return_driver_person_id").optional(),
    notes: string().optional(),
    createdByPersonID: string().from("created_by_person_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const vehicleRelationships = relationships(vehicles, ({ many }) => ({
    images: many({
        sourceField: ["id"],
        destSchema: vehicleImages,
        destField: ["vehicleID"],
    }),
    documents: many({
        sourceField: ["id"],
        destSchema: vehicleDocuments,
        destField: ["vehicleID"],
    }),
    serviceRecords: many({
        sourceField: ["id"],
        destSchema: vehicleServiceRecords,
        destField: ["vehicleID"],
    }),
    damages: many({
        sourceField: ["id"],
        destSchema: vehicleDamages,
        destField: ["vehicleID"],
    }),
    bookings: many({
        sourceField: ["id"],
        destSchema: vehicleBookings,
        destField: ["vehicleID"],
    }),
}));
const vehicleBookingRelationships = relationships(vehicleBookings, ({ one }) => ({
    vehicle: one({
        sourceField: ["vehicleID"],
        destSchema: vehicles,
        destField: ["id"],
    }),
    project: one({
        sourceField: ["projectID"],
        destSchema: projects,
        destField: ["id"],
    }),
    driver: one({
        sourceField: ["driverPersonID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    returnDriver: one({
        sourceField: ["returnDriverPersonID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const vehicleDocumentRelationships = relationships(vehicleDocuments, ({ one }) => ({
    uploadedBy: one({
        sourceField: ["uploadedByPersonID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const vehicleDamageRelationships = relationships(vehicleDamages, ({ one }) => ({
    reportedBy: one({
        sourceField: ["reportedByPersonID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
export const vehicleTables = [
    vehicles,
    vehicleImages,
    vehicleDocuments,
    vehicleServiceRecords,
    vehicleDamages,
    vehicleBookings,
    peopleTable,
    projects,
];
export const vehicleRelationshipsList = [
    vehicleRelationships,
    vehicleDocumentRelationships,
    vehicleDamageRelationships,
    vehicleBookingRelationships,
];
export const vehicleSchema = createSchema({
    tables: vehicleTables,
    relationships: vehicleRelationshipsList,
});
export const vehicleZql = createBuilder(vehicleSchema);
const defineVehicleQuery = defineQueryWithType();
const defineVehicleMutator = defineMutatorWithType();
function canAccessVehicles(ctx) {
    return ctx.role === "admin" || ctx.role === "employee";
}
function assertVehicleWriteAccess(ctx) {
    if (!canAccessVehicles(ctx)) {
        throw new Error("Not allowed to edit vehicles");
    }
}
export const vehicleQueryDefinitions = {
    vehicles: {
        list: defineVehicleQuery(({ ctx }) => {
            if (!canAccessVehicles(ctx)) {
                return vehicleZql.vehicles.where("id", "__unauthorized__");
            }
            return vehicleZql.vehicles
                .where("deletedAt", "IS", null)
                .related("images", (images) => images.orderBy("displayOrder", "asc"))
                .related("damages", (damages) => damages.where("status", "open"))
                .orderBy("name", "asc");
        }),
        byID: defineVehicleQuery(({ args, ctx }) => {
            if (!canAccessVehicles(ctx)) {
                return vehicleZql.vehicles.where("id", "__unauthorized__").one();
            }
            return vehicleZql.vehicles
                .where("id", args.id)
                .related("images", (images) => images.orderBy("displayOrder", "asc"))
                .related("documents", (documents) => documents.related("uploadedBy").orderBy("createdAt", "desc"))
                .related("serviceRecords", (records) => records.orderBy("serviceDate", "desc"))
                .related("damages", (damages) => damages.related("reportedBy").orderBy("reportedOn", "desc"))
                .one();
        }),
    },
    vehicleBookings: {
        byProject: defineVehicleQuery(({ args, ctx, }) => {
            if (!canAccessVehicles(ctx)) {
                return vehicleZql.vehicleBookings.where("id", "__unauthorized__");
            }
            return vehicleZql.vehicleBookings
                .where("projectID", args.projectID)
                .related("vehicle", (vehicle) => vehicle.related("images", (images) => images.orderBy("displayOrder", "asc")))
                .related("driver")
                .related("returnDriver")
                .orderBy("startDate", "asc");
        }),
        byVehicle: defineVehicleQuery(({ args, ctx, }) => {
            if (!canAccessVehicles(ctx)) {
                return vehicleZql.vehicleBookings.where("id", "__unauthorized__");
            }
            return vehicleZql.vehicleBookings
                .where("vehicleID", args.vehicleID)
                .related("project")
                .related("driver")
                .related("returnDriver")
                .orderBy("startDate", "asc");
        }),
        /** Bookings overlapping [from, to] (Zero date numbers, inclusive) */
        inRange: defineVehicleQuery(({ args, ctx, }) => {
            if (!canAccessVehicles(ctx)) {
                return vehicleZql.vehicleBookings.where("id", "__unauthorized__");
            }
            return vehicleZql.vehicleBookings
                .where("startDate", "<=", args.to)
                .where("endDate", ">=", args.from)
                .related("project")
                .related("driver")
                .related("returnDriver")
                .orderBy("startDate", "asc");
        }),
    },
};
export const vehicleQueries = defineQueries(vehicleQueryDefinitions);
// --- Input schemas ---------------------------------------------------------
const uuidSchema = z.string().uuid();
const dateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional();
const textSchema = z.string().trim().max(2000).nullable().optional();
const decimalSchema = z.number().min(0).max(1000).nullable().optional();
const intSchema = z
    .number()
    .int()
    .min(0)
    .max(100_000_000)
    .nullable()
    .optional();
const vehicleFieldsShape = {
    name: z.string().trim().min(1).max(200),
    brand: textSchema,
    model: textSchema,
    vehicleType: z.enum(["car", "van", "truck", "trailer", "ob_van", "other"]),
    status: z.enum(["active", "in_service", "out_of_service"]),
    licensePlate: textSchema,
    registrationNumber: textSchema,
    vin: textSchema,
    firstRegistration: dateSchema,
    licenseCategory: textSchema,
    lengthM: decimalSchema,
    widthM: decimalSchema,
    heightM: decimalSchema,
    cargoLengthM: decimalSchema,
    cargoWidthM: decimalSchema,
    cargoHeightM: decimalSchema,
    emptyWeightKg: intSchema,
    grossWeightKg: intSchema,
    payloadKg: intSchema,
    towingCapacityKg: intSchema,
    seats: z.number().int().min(0).max(100).nullable().optional(),
    fuelType: z
        .enum(["diesel", "petrol", "electric", "hybrid", "other"])
        .nullable()
        .optional(),
    transmission: z.enum(["manual", "automatic"]).nullable().optional(),
    mileageKm: intSchema,
    hasTowHitch: z.boolean(),
    hasPowerInverter: z.boolean(),
    nextInspectionDate: dateSchema,
    nextServiceDate: dateSchema,
    nextServiceKm: intSchema,
    vignetteValidUntil: dateSchema,
    insuranceValidUntil: dateSchema,
    notes: z.string().max(10_000).nullable().optional(),
};
const VEHICLE_DATE_FIELDS = [
    "firstRegistration",
    "nextInspectionDate",
    "nextServiceDate",
    "vignetteValidUntil",
    "insuranceValidUntil",
];
export const createVehicleInputSchema = z.object({
    id: uuidSchema,
    ...vehicleFieldsShape,
    vehicleType: vehicleFieldsShape.vehicleType.optional(),
    status: vehicleFieldsShape.status.optional(),
    hasTowHitch: z.boolean().optional(),
    hasPowerInverter: z.boolean().optional(),
});
export const updateVehicleInputSchema = z.object({ id: uuidSchema }).extend(Object.fromEntries(Object.entries(vehicleFieldsShape).map(([key, schema]) => [
    key,
    schema.optional(),
])));
const vehicleIDInputSchema = z.object({ id: uuidSchema });
export const createVehicleImageInputSchema = z.object({
    id: uuidSchema,
    vehicleID: uuidSchema,
    imageURL: z.string().url().max(2000),
    displayOrder: z.number().int().min(0).optional(),
});
const serviceRecordFieldsShape = {
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mileageKm: intSchema,
    workshop: textSchema,
    description: z.string().trim().min(1).max(10_000),
    cost: z.number().min(0).max(10_000_000).nullable().optional(),
};
export const createVehicleServiceRecordInputSchema = z.object({
    id: uuidSchema,
    vehicleID: uuidSchema,
    ...serviceRecordFieldsShape,
});
export const updateVehicleServiceRecordInputSchema = z.object({
    id: uuidSchema,
    serviceDate: serviceRecordFieldsShape.serviceDate.optional(),
    mileageKm: intSchema,
    workshop: textSchema,
    description: serviceRecordFieldsShape.description.optional(),
    cost: serviceRecordFieldsShape.cost,
});
const damageFieldsShape = {
    title: z.string().trim().min(1).max(500),
    description: z.string().max(10_000).nullable().optional(),
    severity: z.enum(["minor", "major", "critical"]),
    reportedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    imageURL: z.string().url().max(2000).nullable().optional(),
};
export const createVehicleDamageInputSchema = z.object({
    id: uuidSchema,
    vehicleID: uuidSchema,
    ...damageFieldsShape,
    severity: damageFieldsShape.severity.optional(),
    reportedOn: damageFieldsShape.reportedOn.optional(),
    reportedByPersonID: uuidSchema.nullable().optional(),
});
export const updateVehicleDamageInputSchema = z.object({
    id: uuidSchema,
    title: damageFieldsShape.title.optional(),
    description: damageFieldsShape.description,
    severity: damageFieldsShape.severity.optional(),
    reportedOn: damageFieldsShape.reportedOn.optional(),
    imageURL: damageFieldsShape.imageURL,
    status: z.enum(["open", "resolved"]).optional(),
    /** Set automatically when the status changes; can be overridden */
    resolvedOn: dateSchema,
});
export const updateVehicleDocumentInputSchema = z.object({
    id: uuidSchema,
    documentType: z.enum([
        "registration",
        "insurance",
        "leasing",
        "invoice",
        "other",
    ]),
});
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const bookingShape = {
    vehicleID: uuidSchema,
    category: z.enum([
        "project",
        "service",
        "tyre_change",
        "inspection",
        "cleaning",
        "other",
    ]),
    projectID: uuidSchema.nullable().optional(),
    title: z.string().trim().max(500).nullable().optional(),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    outboundDate: isoDateSchema.nullable().optional(),
    returnDate: isoDateSchema.nullable().optional(),
    driverPersonID: uuidSchema.nullable().optional(),
    returnDriverPersonID: uuidSchema.nullable().optional(),
    notes: z.string().max(10_000).nullable().optional(),
};
/** Project bookings need a project, appointments a title; end ≥ start. */
function validateBooking(booking, ctx) {
    if (booking.outboundDate &&
        booking.returnDate &&
        booking.returnDate < booking.outboundDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "The return date must not be before the outbound date",
            path: ["returnDate"],
        });
    }
    if (booking.startDate &&
        booking.endDate &&
        booking.endDate < booking.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "The end date must not be before the start date",
            path: ["endDate"],
        });
    }
    if (booking.category === "project" && !booking.projectID) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Project bookings need a project",
            path: ["projectID"],
        });
    }
    if (booking.category !== "project" && !booking.title?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Appointments need a title",
            path: ["title"],
        });
    }
}
// Create and update both take the full booking so the rules can be checked
export const saveVehicleBookingInputSchema = z
    .object({ id: uuidSchema, ...bookingShape })
    .superRefine(validateBooking);
function normalizeBooking(args) {
    const isProject = args.category === "project";
    return {
        ...args,
        projectID: isProject ? args.projectID : null,
        title: isProject ? null : args.title?.trim(),
        driverPersonID: args.driverPersonID ?? null,
        returnDriverPersonID: isProject
            ? (args.returnDriverPersonID ?? null)
            : null,
        notes: args.notes ?? null,
        startDate: toZeroDate(args.startDate),
        endDate: toZeroDate(args.endDate),
        outboundDate: isProject ? (toZeroDate(args.outboundDate) ?? null) : null,
        returnDate: isProject ? (toZeroDate(args.returnDate) ?? null) : null,
    };
}
// --- Mutators --------------------------------------------------------------
/** Converts ISO date strings of the given fields to Zero date numbers. */
function withZeroDates(args, fields) {
    const result = { ...args };
    for (const field of fields) {
        if (field in result) {
            result[field] = toZeroDate(result[field]);
        }
    }
    return result;
}
function todayIsoDate() {
    return new Date().toISOString().slice(0, 10);
}
export const vehicleMutatorDefinitions = {
    vehicles: {
        create: defineVehicleMutator(createVehicleInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicles.insert({
                vehicleType: "van",
                status: "active",
                hasTowHitch: false,
                hasPowerInverter: false,
                ...withZeroDates(args, VEHICLE_DATE_FIELDS),
            });
        }),
        update: defineVehicleMutator(updateVehicleInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicles.update(withZeroDates(args, VEHICLE_DATE_FIELDS));
        }),
        softDelete: defineVehicleMutator(vehicleIDInputSchema, async ({ tx, ctx, args, }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicles.update({ id: args.id, deletedAt: Date.now() });
        }),
    },
    vehicleImages: {
        create: defineVehicleMutator(createVehicleImageInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleImages.insert({
                ...args,
                displayOrder: args.displayOrder ?? 0,
            });
        }),
        delete: defineVehicleMutator(vehicleIDInputSchema, async ({ tx, ctx, args, }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleImages.delete({ id: args.id });
        }),
    },
    vehicleDocuments: {
        // Documents are uploaded and deleted through the API (private files);
        // only the document type can be changed here.
        update: defineVehicleMutator(updateVehicleDocumentInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleDocuments.update(args);
        }),
    },
    vehicleServiceRecords: {
        create: defineVehicleMutator(createVehicleServiceRecordInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleServiceRecords.insert(withZeroDates(args, ["serviceDate"]));
        }),
        update: defineVehicleMutator(updateVehicleServiceRecordInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleServiceRecords.update(withZeroDates(args, ["serviceDate"]));
        }),
        delete: defineVehicleMutator(vehicleIDInputSchema, async ({ tx, ctx, args, }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleServiceRecords.delete({ id: args.id });
        }),
    },
    vehicleDamages: {
        create: defineVehicleMutator(createVehicleDamageInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleDamages.insert({
                severity: "minor",
                status: "open",
                ...withZeroDates({ ...args, reportedOn: args.reportedOn ?? todayIsoDate() }, ["reportedOn"]),
            });
        }),
        update: defineVehicleMutator(updateVehicleDamageInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            const update = { ...args };
            if (args.status !== undefined && args.resolvedOn === undefined) {
                update.resolvedOn =
                    args.status === "resolved" ? todayIsoDate() : null;
            }
            await tx.mutate.vehicleDamages.update(withZeroDates(update, ["reportedOn", "resolvedOn"]));
        }),
        delete: defineVehicleMutator(vehicleIDInputSchema, async ({ tx, ctx, args, }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleDamages.delete({ id: args.id });
        }),
    },
    vehicleBookings: {
        create: defineVehicleMutator(saveVehicleBookingInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            const booking = normalizeBooking(args);
            await assertDriversBookedOnProject(tx, booking);
            await tx.mutate.vehicleBookings.insert({
                ...booking,
                createdByPersonID: ctx.personID ?? null,
            });
        }),
        update: defineVehicleMutator(saveVehicleBookingInputSchema, async ({ tx, ctx, args }) => {
            assertVehicleWriteAccess(ctx);
            const booking = normalizeBooking(args);
            const previous = await tx.run(vehicleZql.vehicleBookings.where("id", args.id).one());
            // Only check drivers that change (or all when the project changes),
            // so a booking whose driver is no longer booked stays editable
            const projectChanged = previous?.projectID !== booking.projectID;
            await assertDriversBookedOnProject(tx, booking, {
                outbound: projectChanged ||
                    previous?.driverPersonID !== booking.driverPersonID,
                return: projectChanged ||
                    previous?.returnDriverPersonID !== booking.returnDriverPersonID,
            });
            await tx.mutate.vehicleBookings.update(booking);
        }),
        delete: defineVehicleMutator(vehicleIDInputSchema, async ({ tx, ctx, args, }) => {
            assertVehicleWriteAccess(ctx);
            await tx.mutate.vehicleBookings.delete({ id: args.id });
        }),
    },
};
/** Drivers of project bookings must be booked on the project (server only). */
async function assertDriversBookedOnProject(tx, booking, check = { outbound: true, return: true }) {
    if (tx.location !== "server")
        return;
    if (booking.category !== "project" || !booking.projectID)
        return;
    const drivers = new Set();
    if (check.outbound && booking.driverPersonID) {
        drivers.add(booking.driverPersonID);
    }
    if (check.return && booking.returnDriverPersonID) {
        drivers.add(booking.returnDriverPersonID);
    }
    for (const personID of drivers) {
        const availability = await tx.run(projectZql.projectAvailability
            .where("projectID", booking.projectID)
            .where("personID", personID)
            .where("status", "booked")
            .one());
        if (!availability) {
            throw new Error("Drivers must be booked on this project");
        }
    }
}
export const vehicleMutators = defineMutators(vehicleMutatorDefinitions);
